"use client"

/**
 * Modal chỉnh khung hiển thị — dùng chung cho Post và Letáky.
 *
 * - "publish": chưa live → chọn bắt đầu (trống = đăng ngay) + mốc gỡ (tùy chọn)
 * - "edit":    đã lên lịch → sửa cả 2 mốc (bắt đầu bắt buộc)
 * - "end":     đang live → chỉ chỉnh mốc gỡ (bắt buộc; bỏ lịch qua nút riêng)
 */

import { t } from "@/languages/i18n"
import { App, Button, Modal } from "antd"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { DisplayWindow, DisplayWindowFields, validateDisplayWindow } from "./display-window"

export type DisplayWindowMode = "publish" | "edit" | "end"

type Suggestion = { label: string; value: string } | null

interface DisplayWindowModalProps {
  mode: DisplayWindowMode | null
  onClose: () => void
  /** Khung đang lưu (start = lịch đăng nếu chưa live, end = lịch gỡ). */
  current: DisplayWindow
  canPublish: boolean
  canSchedule: boolean
  loading?: boolean
  /** Tiêu đề cho mode "publish", vd. "Xuất bản bài viết". */
  publishTitle: string
  onPublishNow: (end: string | null) => void
  onSchedule: (start: string, end: string | null) => void
  /** end = null nghĩa là bỏ lịch gỡ. */
  onSaveEnd: (end: string | null) => void
  startSuggestion?: Suggestion
  endSuggestion?: Suggestion
}

export const DisplayWindowModal = ({
  mode,
  onClose,
  current,
  canPublish,
  canSchedule,
  loading = false,
  publishTitle,
  onPublishNow,
  onSchedule,
  onSaveEnd,
  startSuggestion,
  endSuggestion,
}: DisplayWindowModalProps) => {
  const { message } = App.useApp()
  const [draft, setDraft] = useState<DisplayWindow>({ start: null, end: null })

  useEffect(() => {
    if (!mode) return
    setDraft({
      start: mode === "edit" ? current.start : null,
      end: mode === "publish" ? null : current.end,
    })
    // Chỉ reset khi mở modal; current đổi lúc đang mở không được đè lựa chọn của user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // Modal "publish": giờ bắt đầu <= hiện tại coi như xuất bản ngay (không phải lên lịch).
  // Tính lại mỗi lần render/submit để bám theo thời gian thực.
  const isScheduling = () => mode === "publish" && !!draft.start && dayjs(draft.start).isAfter(dayjs())
  const effectiveStart = () => (mode === "publish" && !isScheduling() ? null : draft.start)

  // Cả hai mốc đều có giá trị → mốc gỡ phải sau mốc bắt đầu; báo ngay dưới ô.
  const endError =
    draft.start && draft.end && !dayjs(draft.end).isAfter(dayjs(draft.start))
      ? t("Thời điểm gỡ phải sau thời điểm bắt đầu hiển thị")
      : null

  const submit = () => {
    if (endError) return
    if (mode === "edit" && !draft.start) {
      message.error(t("Vui lòng chọn thời điểm bắt đầu hiển thị"))
      return
    }
    // Bỏ lịch gỡ đi qua nút "Bỏ lịch gỡ" riêng; Lưu trống không được hiểu là bỏ lịch.
    if (mode === "end" && !draft.end) {
      message.error(t("Vui lòng chọn thời điểm gỡ bài"))
      return
    }

    const start = effectiveStart()
    const error = validateDisplayWindow({ start, end: draft.end })
    if (error) {
      message.error(error)
      return
    }

    if (mode === "end") {
      onSaveEnd(draft.end)
    } else if (start) {
      onSchedule(start, draft.end)
    } else {
      onPublishNow(draft.end)
    }
    onClose()
  }

  // Nút chính đổi theo lựa chọn: bắt đầu ở tương lai = lên lịch, trống/đã qua = xuất bản ngay.
  const scheduling = isScheduling()
  const submitLabel =
    mode === "end"
      ? t("Lưu")
      : mode === "edit"
        ? t("Lưu thời gian hiển thị")
        : scheduling
          ? t("Lên lịch xuất bản")
          : t("Xuất bản ngay")
  const submitAllowed =
    !endError &&
    (mode === "end"
      ? canSchedule && Boolean(draft.end)
      : mode === "edit" || scheduling
        ? canSchedule
        : canPublish)

  const title = mode === "end" ? t("Lịch gỡ bài") : mode === "edit" ? t("Sửa thời gian hiển thị") : publishTitle

  return (
    <Modal
      open={mode !== null}
      title={title}
      width={500}
      destroyOnClose
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t("Hủy")}</Button>
          {mode === "end" && current.end && (
            <Button
              danger
              onClick={() => {
                onSaveEnd(null)
                onClose()
              }}
              loading={loading}
            >
              {t("Bỏ lịch gỡ")}
            </Button>
          )}
          <Button type="primary" onClick={submit} loading={loading} disabled={!submitAllowed}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <div className="mt-4">
        <DisplayWindowFields
          value={draft}
          onChange={setDraft}
          hideStart={mode === "end"}
          endRequired={mode === "end"}
          startPlaceholder={mode === "edit" ? t("Chọn ngày giờ đăng") : undefined}
          startSuggestion={startSuggestion}
          endSuggestion={endSuggestion}
          endError={endError}
        />
      </div>
    </Modal>
  )
}

export default DisplayWindowModal
